import React from 'react';
import { AgnesAlert, AgnesAlertContent, AgnesBadge, AgnesCheckbox } from '@agnes/ui';
import { Info, Rows3 } from 'lucide-react';
import { BaseComponentProps } from '@/components/BaseComponentProps';
import { Rubrica } from '@/types/rubricas/Rubrica';
import {
  agruparRubricasAfetadas,
  CAMPOS_INCIDENCIA,
  formatarCompetencia,
  type NaturezaCatalogo,
  valorComum,
} from '@/pages/folha-pagamento/acao-em-massa/acaoEmMassa';
import { SecaoRecolhivel } from '@/pages/folha-pagamento/acao-em-massa/SecaoRecolhivel';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';
import { AnaliseGrauExposicaoRisco } from '@/types/analise/AnaliseGrauExposicaoRisco';

export interface PainelRubricasAfetadasProps extends BaseComponentProps {
  rubricas: Rubrica[];
  removidas: string[];
  onRemovidasChange: (removidas: string[]) => void;
  naturezas: NaturezaCatalogo[];
}

const VARIOS = 'Vários';
const TH = 'whitespace-nowrap p-3 font-semibold';
const TD = 'p-3 align-middle';

/** Valor comum às rubricas da natureza; "Vários" quando divergem. */
function comum(rubricas: Rubrica[], valor: (rubrica: Rubrica) => string | null) {
  const unico = valorComum(rubricas.map((rubrica) => valor(rubrica) ?? ''));
  return unico === undefined ? VARIOS : unico || '—';
}

export function PainelRubricasAfetadas({
  rubricas,
  removidas,
  onRemovidasChange,
  naturezas,
  dataQa = 'acao-massa-afetadas',
}: PainelRubricasAfetadasProps) {
  const grupos = React.useMemo(() => agruparRubricasAfetadas(rubricas), [rubricas]);
  const [gruposFechados, setGruposFechados] = React.useState<string[]>([]);

  const chaves = grupos.flatMap((grupo) => grupo.naturezas.map((natureza) => natureza.chave));
  const removidasSet = new Set(removidas);
  const selecionadas = chaves.filter((chave) => !removidasSet.has(chave)).length;
  const marcarTodas = (marcar: boolean) => onRemovidasChange(marcar ? [] : chaves);
  const alternar = (novas: string[], marcar: boolean) => {
    const next = new Set(removidas);
    novas.forEach((chave) => (marcar ? next.delete(chave) : next.add(chave)));
    onRemovidasChange([...next]);
  };
  const estado = (total: number, marcadas: number) =>
    marcadas === total && total > 0 ? true : marcadas > 0 ? 'indeterminate' : false;

  return (
    <SecaoRecolhivel
      dataQa={dataQa}
      titulo="Rubricas Afetadas"
      icone={<Rows3 className="h-5 w-5" />}
      className="border-[1.5px] border-agnes-primary"
      selo={
        <span data-qa={`${dataQa}-total`} className="text-sm text-agnes-text-muted">
          {selecionadas} de {chaves.length} selecionadas
        </span>
      }
    >
      <div className="flex flex-col gap-3 p-5">
        <AgnesAlert
          dataQa={`${dataQa}-aviso`}
          icon={<Info className="h-4 w-4 text-agnes-primary" />}
          className="bg-agnes-primary/5"
        >
          <AgnesAlertContent dataQa={`${dataQa}-aviso`}>
            Você está impactando <b>{rubricas.length.toLocaleString('pt-BR')} registros</b> com essa
            ação em{' '}
            <b>
              {chaves.length} {chaves.length === 1 ? 'rubrica' : 'rubricas'}
            </b>
            . Rubricas idênticas são tratadas uma única vez — não repetimos iguais.
          </AgnesAlertContent>
        </AgnesAlert>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <AgnesCheckbox
            dataQa={`${dataQa}-todas`}
            label={<b>Selecionar todas as rubricas</b>}
            checked={estado(chaves.length, selecionadas)}
            onCheckedChange={() => marcarTodas(selecionadas !== chaves.length)}
          />
          <span className="text-xs text-agnes-text-muted">
            Selecione quais rubricas serão de fato alteradas
          </span>
        </div>

        {grupos.map((grupo) => {
          const grupoQa = `${dataQa}-grupo-${encodeURIComponent(grupo.descricao)}`;
          const chavesGrupo = grupo.naturezas.map((natureza) => natureza.chave);
          const marcadas = chavesGrupo.filter((chave) => !removidasSet.has(chave)).length;
          const aberto = !gruposFechados.includes(grupo.descricao);
          return (
            <div
              key={grupo.descricao}
              data-qa={grupoQa}
              className="rounded-lg border border-agnes-border"
            >
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                <div className="flex items-center gap-2">
                  <AgnesCheckbox
                    dataQa={`${grupoQa}-selecao`}
                    aria-label={`Selecionar rubricas de ${grupo.descricao}`}
                    checked={estado(chavesGrupo.length, marcadas)}
                    onCheckedChange={() => alternar(chavesGrupo, marcadas !== chavesGrupo.length)}
                  />
                  <span className="font-semibold text-agnes-primary">{grupo.descricao}</span>
                  <AgnesBadge size="sm" variant="secondary">
                    {chavesGrupo.length} {chavesGrupo.length === 1 ? 'rubrica' : 'rubricas'}
                  </AgnesBadge>
                </div>
                <button
                  type="button"
                  data-qa={`${grupoQa}-detalhes`}
                  aria-expanded={aberto}
                  className="rounded-md px-2 py-1 text-sm font-semibold text-agnes-text-primary hover:bg-[rgba(68,45,150,0.05)]"
                  onClick={() =>
                    setGruposFechados((atual) =>
                      aberto
                        ? [...atual, grupo.descricao]
                        : atual.filter((d) => d !== grupo.descricao),
                    )
                  }
                >
                  {aberto ? 'Ocultar detalhes' : 'Ver detalhes'}
                </button>
              </div>

              {aberto && (
                <div className="max-w-full overflow-x-auto border-t border-agnes-border">
                  <table className="w-full min-w-[1000px] text-left text-sm text-agnes-text-tables">
                    <thead className="bg-agnes-bg-table-header text-xs text-agnes-text-secondary">
                      <tr>
                        <th scope="col" className="w-11 p-3" />
                        <th scope="col" className={TH}>
                          Código da natureza
                        </th>
                        <th scope="col" className={TH}>
                          Nome da natureza
                        </th>
                        <th scope="col" className={TH}>
                          Descrição da rubrica
                        </th>
                        <th scope="col" className={TH}>
                          Início validade
                        </th>
                        <th scope="col" className={TH}>
                          Fim validade
                        </th>
                        {CAMPOS_INCIDENCIA.map(({ key }) => (
                          <th key={key} scope="col" className={`${TH} text-center`}>
                            {key.toUpperCase()}
                          </th>
                        ))}
                        <th scope="col" className={`${TH} text-center`}>
                          Teto
                        </th>
                        <th scope="col" className={TH}>
                          Grau de risco
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--tp-border)]">
                      {grupo.naturezas.map((natureza) => {
                        const marcada = !removidasSet.has(natureza.chave);
                        const linhaQa = `${grupoQa}-natureza-${natureza.codigo ?? 'sem-codigo'}`;
                        const risco = valorComum(natureza.rubricas.map((r) => r.grauRisco));
                        const teto = naturezas.find(
                          (n) => n.codigo === String(natureza.codigo),
                        )?.teto;
                        return (
                          <tr
                            key={natureza.chave}
                            data-qa={linhaQa}
                            className={marcada ? 'bg-agnes-primary/5' : 'opacity-50'}
                          >
                            <td className={TD}>
                              <AgnesCheckbox
                                dataQa={`${linhaQa}-selecao`}
                                aria-label={`Selecionar natureza ${natureza.codigo ?? 'sem código'}`}
                                checked={marcada}
                                onCheckedChange={() => alternar([natureza.chave], !marcada)}
                              />
                            </td>
                            <td className={`${TD} font-bold text-agnes-text-primary`}>
                              {natureza.codigo ?? '—'}
                            </td>
                            <td className={TD}>
                              {natureza.nome || '—'}{' '}
                              <span className="text-xs text-agnes-text-muted">
                                · {natureza.rubricas.length.toLocaleString('pt-BR')}{' '}
                                {natureza.rubricas.length === 1 ? 'registro' : 'registros'}
                              </span>
                            </td>
                            <td className={TD}>{grupo.descricao}</td>
                            <td className={`${TD} whitespace-nowrap text-xs`}>
                              {comum(natureza.rubricas, (r) =>
                                formatarCompetencia(r.inicioValidade),
                              )}
                            </td>
                            <td className={`${TD} whitespace-nowrap text-xs`}>
                              {comum(natureza.rubricas, (r) => formatarCompetencia(r.fimValidade))}
                            </td>
                            {CAMPOS_INCIDENCIA.map(({ key }) => (
                              <td key={key} className={`${TD} text-center`}>
                                {comum(natureza.rubricas, (r) => r[key])}
                              </td>
                            ))}
                            <td className={`${TD} text-center`}>{teto || '—'}</td>
                            <td className={TD}>
                              {risco === undefined ? (
                                VARIOS
                              ) : (
                                <BadgeGrauExposicaoRisco
                                  grau={risco as AnaliseGrauExposicaoRisco}
                                />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SecaoRecolhivel>
  );
}
